<template>
  <div class="daily-view" :class="{ 'today': isToday }">
    <div class="day-header">
      <h3>{{ formattedDate }}</h3>
    </div>
    <div class="time-grid">
      <div 
        v-for="timeSlot in timeSlots" 
        :key="timeSlot.label"
        class="time-slot"
        :style="{ height: timeSlotHeight }"
      >
        <div class="time-label">{{ timeSlot.label }}</div>
        <div class="time-content" :class="{ 'weekend': isWeekend }">
          <div 
            v-for="session in getSessionsForTime(timeSlot.start, timeSlot.end)" 
            :key="session.session_id"
            class="session-item"
            @click="$emit('session-click', session)"
          >
            <div class="session-time">
              {{ formatTime(session.start_at) }} - {{ formatTime(session.end_at) }}
            </div>
            <div class="session-course">{{ session.course.name }}</div>
            <div class="session-school">{{ session.school.name }}</div>
            <div class="session-status">
              <div 
                v-if="session.absentStudents.length > 0" 
                class="session-absent"
                :class="{ 'all-completed': session.absentStudents.length === session.completedMakeupClassStudents?.length }"
              >
                {{ session.absentStudents.length }} absent
              </div>
              <div 
                v-if="session.completedMakeupClassStudents?.length > 0" 
                class="makeup-completed"
                :title="`${session.completedMakeupClassStudents.length} makeup classes completed`"
              >
                ✓ {{ session.completedMakeupClassStudents.length }}/{{ session.absentStudents.length }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  sessions: Array,
  currentDate: Date
});

const timeSlotHeight = '200px'; // Fixed height for each time slot

const isToday = computed(() => {
  const today = new Date();
  return (
    props.currentDate.getDate() === today.getDate() &&
    props.currentDate.getMonth() === today.getMonth() &&
    props.currentDate.getFullYear() === today.getFullYear()
  );
});

const formattedDate = computed(() => {
  return props.currentDate.toLocaleDateString('default', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
});

const timeSlots = computed(() => {
  const periods = [
    { label: 'Morning (8-12)', startHour: 8, endHour: 12 },
    { label: 'Afternoon (12-6)', startHour: 12, endHour: 18 },
    { label: 'Evening (6-10)', startHour: 18, endHour: 22 }
  ];

  return periods.map(period => {
    const start = new Date(props.currentDate);
    start.setHours(period.startHour, 0, 0, 0);
    const end = new Date(props.currentDate);
    end.setHours(period.endHour, 0, 0, 0);
    
    return {
      label: period.label,
      start,
      end,
      durationHours: period.endHour - period.startHour
    };
  });
});

const isWeekend = computed(() => {
  const day = props.currentDate.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
});

function getSessionsForTime(start, end) {
  return props.sessions.filter(session => {
    const sessionStart = new Date(session.start_at);
    const sessionEnd = new Date(session.end_at);
    return sessionStart >= start && sessionStart < end;
  }).sort((a, b) => {
    return new Date(a.start_at) - new Date(b.start_at);
  });
}

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<style scoped>
.daily-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.daily-view.today {
  background-color: #e3f2fd; /* Light blue for today */
}

.day-header {
  padding: 15px;
  background: #f5f5f5;
  text-align: center;
}

.time-grid {
  flex-grow: 1;
  overflow-y: auto;
}

.time-slot {
  display: flex;
  min-height: 200px; /* Minimum height for empty slots */
  border-bottom: 1px solid #eee;
}

.time-label {
  width: 120px;
  padding: 10px;
  text-align: right;
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
  background: #f9f9f9;
  border-right: 1px solid #eee;
}

.time-content {
  position: relative;
  flex-grow: 1;
  min-height: 100%;
  overflow-y: auto;
  padding: 5px;
}

.time-content.weekend {
  background-color: #fff0f5; /* Light pink for weekends */
}

.session-item {
  background: var(--primary-color);
  color: white;
  padding: 3px 5px;
  margin-bottom: 3px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
}

.session-item:hover {
  opacity: 0.9;
}

.session-time {
  font-weight: bold;
  font-size: 0.8rem;
  margin-bottom: 3px;
}

.session-course {
  font-size: 0.9rem;
  margin-bottom: 2px;
}

.session-school {
  font-size: 0.7rem;
  opacity: 0.8;
  margin-bottom: 3px;
}

.session-status {
  display: flex;
  gap: 4px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.session-absent {
  font-size: 0.7rem;
  background: var(--danger-color); /* Default red for incomplete */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}

.session-absent.all-completed {
  background: #D7A3A3; /* Gray when all makeup classes are done */
  color: #5D1F1F;
}

.makeup-completed {
  font-size: 0.7rem;
  background: var(--secondary-color); /* Blue for completed */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}
</style>